import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Select } from '../atoms/Select/Select';
import { Stack } from '../atoms/Stack/Stack';
import { Heading } from '../atoms/Typography/Heading';
import { SUPPORTED_CURRENCIES, currencyLabel, type Currency } from '../../config/currencies';
import { useCurrency } from '../../hooks/useCurrency';
import { USER_PREFERENCES_QUERY_KEY, updateUserPreferences } from '../../services/preferencesService';

/** Settings → General: the currency budget amounts are entered and shown in. Saved on change. */
export function CurrencySetting() {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  const queryClient = useQueryClient();
  const currency = useCurrency();
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  const change = async (next: Currency) => {
    setSaving(true);
    setStatus('idle');
    try {
      queryClient.setQueryData(USER_PREFERENCES_QUERY_KEY, await updateUserPreferences({ currency: next }));
      setStatus('saved');
    } catch {
      setStatus('error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap="3">
      <Heading level={3} id="settings-currency-heading">{t('settings.currency.title')}</Heading>
      <Select
        id="settings-currency"
        aria-labelledby="settings-currency-heading"
        hint={t('settings.currency.hint')}
        value={currency}
        options={SUPPORTED_CURRENCIES.map((c) => ({ value: c, label: currencyLabel(c, language) }))}
        onChange={(e) => void change(e.target.value as Currency)}
        disabled={saving}
      />
      {status === 'error' && <Alert>{t('settings.currency.saveError')}</Alert>}
      <LiveRegion visuallyHidden>{status === 'saved' ? t('settings.currency.saved') : ''}</LiveRegion>
    </Stack>
  );
}
